import useSubGroupSelector from "features/bots/BotForm/General/useSubGroupSelector.tsx";
import {
  ADMIN_TRADING_CONFIGURATION_PATH,
  MOBILE_SEARCH,
} from "common/constants";
import { BOT_TYPE_TO_BASE_PATH } from "features/bots/common/constants";
import { openMessage } from "utils/misc";
import { BotType, SubGroup } from "@shared/types";
import React, { useContext, useRef, useState } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { useHistory } from "react-router-dom";
import { ErrorText, FormFieldTitle } from "features/bots/common/styles";
import MissingGroupButton from "features/configuration/AccountsAndGroups/MissingGroupButton";
import Select from "common/Select/Select";
import Flex from "common/Boxes/Flex";
import IconWithFallback from "Global/IconWithFallback";
import Text from "common/Typography/Text";
import useMedia from "utils/hooks/useMedia";
import Button from "common/Buttons/Button";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { getGroupExtraDetailsRoute } from "utils/routes";
import styled from "styled-components";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types/common/common.types";
import { useSelectUser } from "@shared/utils/hooks/misc.ts";
import { DefaultOptionType } from "antd/es/select";

import { BOT_FORM_GROUP_SELECT_ID } from "testIds";

interface SubGroupSelectProps {
  setOpenPersonalGroupModal?: (open: boolean) => void;
}

const SubGroupSelect = ({ setOpenPersonalGroupModal }: SubGroupSelectProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { botType, isChannelConfiguration, subGroupId } =
    useContext(BotFormContext)!;
  const [open, setOpen] = useState(false);
  const buttonHoveredRef = useRef(false);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { historyPush } = useHistoryWithQuery();
  const history = useHistory();
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const user = useSelectUser();

  const { subGroupSelectorData, subGroupError, isAdminWorkspace } =
    useSubGroupSelector({
      onInvalidGroup: () => {
        const nextPath = isChannelConfiguration
          ? ADMIN_TRADING_CONFIGURATION_PATH
          : BOT_TYPE_TO_BASE_PATH[botType];
        history.push(nextPath);
      },
      openMessage,
      querySubGroupId: subGroupId,
      setOpenPersonalGroupModal,
    });

  const hideMissingGroupButton =
    user?.isWhopExperience() ||
    (isTradingViewSignalsBot && !isAdminWorkspace) ||
    user?.is_sub_user;

  const filterOption = (input: string, option?: SubGroupOptionType) => {
    return (
      option?.groupName
        ?.toLowerCase()
        .toString()
        .includes(input.toLowerCase()) ?? false
    );
  };

  const onDetailsButtonClick = (groupData: SubGroup) => {
    setOpen(false);
    historyPush(
      getGroupExtraDetailsRoute(location.pathname, groupData.id.toString())
    );
  };

  if (!subGroupSelectorData.shouldShow) return null;

  const options = subGroupSelectorData.options?.map((optionGroupData) => {
    return {
      label: optionGroupData.title,
      options: optionGroupData.options?.map((subGroupData) => {
        const label = (
          <Flex style={{ width: "100%" }}>
            <Flex style={{ justifyContent: "flex-start", overflow: "hidden" }}>
              <IconWithFallback
                iconLink={subGroupData.image}
                iconStyle={{ marginRight: 8, borderRadius: 12 }}
                pxIconSize={18}
              />
              <Text $ellipsis={true}>{subGroupData.name}</Text>
            </Flex>
            {subGroupData.isFreeTrialGroup && (
              <StyledButton
                $type="outlined"
                style={{}}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onDetailsButtonClick(subGroupData);
                }}
                onMouseEnter={() => (buttonHoveredRef.current = true)}
                onMouseLeave={() => (buttonHoveredRef.current = false)}
              >
                {t("buttons.view_details")}
              </StyledButton>
            )}
          </Flex>
        );
        return {
          label,
          value: subGroupData.id,
          groupName: subGroupData.name,
        };
      }),
    };
  });

  return (
    <div>
      <FormFieldTitle style={{ justifyContent: "space-between" }}>
        {subGroupSelectorData.title}
        {!hideMissingGroupButton && <MissingGroupButton isLink={true} />}
      </FormFieldTitle>
      <Select<number, SubGroupOptionType>
        data-testid={BOT_FORM_GROUP_SELECT_ID}
        showSearch={{ filterOption }}
        style={{ width: "100%" }}
        open={open}
        onOpenChange={(isOpen) => {
          if (!buttonHoveredRef.current) {
            setOpen(isOpen);
          }
        }}
        value={subGroupSelectorData.value}
        onChange={subGroupSelectorData.onChange}
        popupMatchSelectWidth={isMobile}
        optionLabelProp="label"
        disabled={subGroupSelectorData.disabled}
        options={options}
        mobileTitle={subGroupSelectorData.title}
      />
      <ErrorText>{subGroupError}</ErrorText>
    </div>
  );
};

export default SubGroupSelect;

interface SubGroupOptionType extends DefaultOptionType {
  groupName?: string;
  options?: SubGroupOptionType[];
}

const StyledButton = styled(Button)`
  height: 22px;
  font-size: 12px;
  padding: 0 12px;
  margin-left: 8px;
  display: flex;
  align-items: center;

  span {
    line-height: 10px;
  }
`;
